import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Scale } from "lucide-react";
import { Badge } from "./ui/badge";
import { Card, CardContent } from "./ui/card";
import { fmtINR } from "../lib/api";

/**
 * Phase 136 — one reconciliation strip for every ranking / activity page.
 * Every report totals the SAME payments table the Collections register uses; this strip shows the unified
 * figure ("All receipts"), how much of it is credited to the staff listed on the page, and names the buckets
 * that make up the difference (supervisors' own receipts, imports without assigned staff, Citizen Portal
 * receipts on unassigned properties, former staff) so the numbers always add up.
 *
 * props: unified {amount, receipts} · credited {amount, receipts} · others {amount, receipts, buckets[]}
 *        creditedLabel — what "credited" means on this page · collectionsHref — deep link to the register
 */
export function CollectionsReconciliation({ unified, credited, others, creditedLabel = "Credited to collectors",
                                            collectionsHref, rangeLabel, testId = "reconciliation", compact = false }) {
  if (!unified) return null;
  const buckets = (others?.buckets || []).filter((b) => b.receipts);
  const ok = Math.abs(((credited?.amount || 0) + (others?.amount || 0)) - (unified.amount || 0)) < 0.01;
  return (
    <Card className="shadow-sm border-primary/20 bg-primary/[0.03]" data-testid={`${testId}-card`}>
      <CardContent className={compact ? "p-3 space-y-2" : "p-4 space-y-3"}>
        <div className="flex flex-wrap items-start gap-x-6 gap-y-2">
          <div className="min-w-[180px]">
            <p className="text-xs text-muted-foreground flex items-center gap-1"><Scale className="h-3.5 w-3.5" /> All receipts{rangeLabel ? ` · ${rangeLabel}` : ""}</p>
            <p className="text-xl font-semibold tabular-nums leading-tight" data-testid={`${testId}-unified-amount`}>{fmtINR(unified.amount || 0)}</p>
            <p className="text-[11px] text-muted-foreground tabular-nums">
              {(unified.receipts || 0).toLocaleString("en-IN")} receipt{unified.receipts === 1 ? "" : "s"}
              {collectionsHref
                ? <> · <Link to={collectionsHref} className="underline underline-offset-2 hover:text-foreground" data-testid={`${testId}-collections-link`}>same as Collections</Link></>
                : " · same as the Collections register"}
            </p>
          </div>
          <div className="min-w-[160px]">
            <p className="text-xs text-muted-foreground">{creditedLabel}</p>
            <p className="text-lg font-semibold tabular-nums leading-tight" data-testid={`${testId}-credited-amount`}>{fmtINR(credited?.amount || 0)}</p>
            <p className="text-[11px] text-muted-foreground tabular-nums">{(credited?.receipts || 0).toLocaleString("en-IN")} receipt{credited?.receipts === 1 ? "" : "s"}</p>
          </div>
          <div className="min-w-[160px]">
            <p className="text-xs text-muted-foreground">Not credited to a collector</p>
            <p className="text-lg font-semibold tabular-nums leading-tight" data-testid={`${testId}-others-amount`}>{fmtINR(others?.amount || 0)}</p>
            <p className="text-[11px] text-muted-foreground tabular-nums">{(others?.receipts || 0).toLocaleString("en-IN")} receipt{others?.receipts === 1 ? "" : "s"}</p>
          </div>
          <div className="ml-auto self-center">
            {ok ? (
              <Badge variant="outline" className="gap-1 bg-emerald-50 text-emerald-800 border-emerald-200 font-normal" data-testid={`${testId}-status`}>
                <CheckCircle2 className="h-3 w-3" /> Totals reconcile
              </Badge>
            ) : (
              <Badge variant="outline" className="gap-1 bg-amber-50 text-amber-800 border-amber-200 font-normal" data-testid={`${testId}-status`}>Check totals</Badge>
            )}
          </div>
        </div>
        {buckets.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid={`${testId}-buckets`}>
            {buckets.map((b) => (
              <Badge key={b.key} variant="outline" className="font-normal text-[11px] tabular-nums bg-background" data-testid={`${testId}-bucket-${b.key}`}
                title={(b.rows || []).slice(0, 8).map((r) => `${r.name}${r.employee_id ? ` (${r.employee_id})` : ""}: ${fmtINR(r.amount)}`).join("\n") || undefined}>
                {b.label}: <span className="font-medium ml-1">{fmtINR(b.amount)}</span><span className="text-muted-foreground ml-1">· {b.receipts}</span>
              </Badge>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default CollectionsReconciliation;
